'use client';

import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { approvedSetQueryKey } from '@shared/utils/hooks/useApprovedSet';
import useAuth from '@shared/utils/hooks/useAuth';
import useSocket from '@shared/utils/hooks/useSocket';
import useUser from '@shared/utils/hooks/useUser';
import { type ApprovedSet, saveApproved } from '@shared/utils/storage/approvedVerificationStorage';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ImagePlus } from 'lucide-react';
import { useEffect, useReducer, useRef } from 'react';
import { downscaleToCanonicalPng } from '../../utils/imageDownscale';
import { client, service } from '../../utils/trpc';
import { downloadBackup } from '../../utils/verificationBackup';
import Button from '../common/Button';
import Upload from '../common/Upload';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import { CandidateList } from './CandidateList';
type State = {
  bioInput: string;
  submittingBio: boolean;
  submittingAvatar: boolean;
  paying: boolean;
  uploadOpen: boolean;
  justApproved: ApprovedSet | null;
};
type Action = {
  type: 'SET_BIO';
  payload: string;
} | {
  type: 'SUBMIT_BIO_START';
} | {
  type: 'SUBMIT_BIO_DONE';
  success: boolean;
} | {
  type: 'SUBMIT_AVATAR_START';
} | {
  type: 'SUBMIT_AVATAR_END';
} | {
  type: 'OPEN_UPLOAD';
} | {
  type: 'CLOSE_UPLOAD';
} | {
  type: 'PAY_START';
} | {
  type: 'PAY_END';
} | {
  type: 'APPROVED';
  payload: ApprovedSet;
} | {
  type: 'DISMISS_APPROVED';
};
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_BIO':
      return {
        ...state,
        bioInput: action.payload
      };
    case 'SUBMIT_BIO_START':
      return {
        ...state,
        submittingBio: true
      };
    case 'SUBMIT_BIO_DONE':
      // Clear input only on success so user can retry with their text on failure
      return {
        ...state,
        submittingBio: false,
        bioInput: action.success ? '' : state.bioInput
      };
    case 'SUBMIT_AVATAR_START':
      return {
        ...state,
        submittingAvatar: true
      };
    case 'SUBMIT_AVATAR_END':
      return {
        ...state,
        submittingAvatar: false
      };
    case 'OPEN_UPLOAD':
      return {
        ...state,
        uploadOpen: true
      };
    case 'CLOSE_UPLOAD':
      return {
        ...state,
        uploadOpen: false
      };
    case 'PAY_START':
      return {
        ...state,
        paying: true
      };
    case 'PAY_END':
      return {
        ...state,
        paying: false
      };
    case 'APPROVED':
      return {
        ...state,
        justApproved: action.payload
      };
    case 'DISMISS_APPROVED':
      return {
        ...state,
        justApproved: null
      };
    default:
      return state;
  }
}
const initialState: State = {
  bioInput: '',
  submittingBio: false,
  submittingAvatar: false,
  paying: false,
  uploadOpen: false,
  justApproved: null
};
export const VerificationSessionScreen = () => {
  const {
    currentUser
  } = useUser();
  const {
    initializeUser
  } = useAuth({
    client,
    service
  });
  const {
    onVerificationSessionChanged,
    onVerificationApproved
  } = useSocket();
  const queryClient = useQueryClient();

  // Toast on absent->present session transition.
  const lastSessionRef = useRef<'absent' | 'present'>('absent');
  const {
    data: session
  } = useQuery({
    queryKey: ['verification.session'],
    queryFn: () => unwrapFromResult(client.verification.session.query()),
    enabled: !!currentUser?.id
  });

  // Public fee/limit constants come from the server. Only read below the
  // `if (!session) return null` guard, so it follows the session rather than
  // firing on every pageview.
  const {
    data: fees
  } = useQuery({
    queryKey: ['verification.fees'],
    queryFn: () => unwrapFromResult(client.verification.getFees.query()),
    staleTime: 60 * 60 * 1000,
    enabled: !!session
  });
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    bioInput,
    submittingBio,
    submittingAvatar,
    paying,
    uploadOpen,
    justApproved
  } = state;

  // Approved is one-shot: offline devices miss it and recover via backup file or re-verify.
  useEffect(() => {
    const userId = currentUser?.id;
    const offSession = onVerificationSessionChanged(() => {
      queryClient.invalidateQueries({
        queryKey: ['verification.session']
      });
    });
    const offApproved = onVerificationApproved(payload => {
      if (!userId) return;
      void (async () => {
        await saveApproved(userId, payload);
        queryClient.invalidateQueries({
          queryKey: approvedSetQueryKey(userId)
        });
        dispatch({
          type: 'APPROVED',
          payload
        });
        await initializeUser();
      })();
    });
    return () => {
      offSession();
      offApproved();
    };
  }, [currentUser?.id, queryClient]);
  useEffect(() => {
    const hasSession = !!session;
    const prev = lastSessionRef.current;
    lastSessionRef.current = hasSession ? 'present' : 'absent';
    if (prev === 'absent' && hasSession) {
      toast('Your verification session is ready, opening now.', 'Admin started session', 'success');
    }
  }, [session]);
  if (justApproved && currentUser?.id) {
    const userId_0 = currentUser.id;
    return <Modal maxWidth={480} closeHandler={() => dispatch({
      type: 'DISMISS_APPROVED'
    })}>
        <ModalHeader closeHandler={() => dispatch({
        type: 'DISMISS_APPROVED'
      })}>
          <div className="flex items-center gap-2 text-emerald-500">
            <Check size={20} />
            <span>Verification Complete</span>
          </div>
        </ModalHeader>
        <ModalContent>
          <p className="text-sm text-textSecondary">
            Your approved bios and avatars are saved on this device only. If you
            clear browser data or switch devices you&apos;ll lose them and need
            to re-verify, unless you save a backup file now.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button text="Download Backup" variant="PRIMARY" onClick={() => downloadBackup(userId_0, justApproved)} />
            <Button text="Done" variant="SECONDARY" onClick={() => dispatch({
            type: 'DISMISS_APPROVED'
          })} />
          </div>
        </ModalContent>
      </Modal>;
  }
  if (!session) return null;
  const freeCandidates = fees?.freeCandidates ?? 0;
  const isFull = session.approvedCount >= freeCandidates + session.paidExtras;

  // No local refetch; socket event invalidates the query.
  const submitBio = async () => {
    if (!bioInput.trim()) return;
    dispatch({
      type: 'SUBMIT_BIO_START'
    });
    let success = false;
    try {
      await client.verification.submitBio.mutate({
        text: bioInput
      });
      success = true;
    } finally {
      dispatch({
        type: 'SUBMIT_BIO_DONE',
        success
      });
    }
  };
  const payForExtra = async () => {
    dispatch({
      type: 'PAY_START'
    });
    try {
      await client.verification.payForExtra.mutate();
    } finally {
      dispatch({
        type: 'PAY_END'
      });
    }
  };

  // Downscale + PNG-encode client-side so the server never sees the raw multi-MB file.
  const submitCroppedAvatar = async (file: File) => {
    dispatch({
      type: 'CLOSE_UPLOAD'
    });
    dispatch({
      type: 'SUBMIT_AVATAR_START'
    });
    try {
      const bytes = await downscaleToCanonicalPng(file);
      await client.verification.submitAvatar.mutate({
        bytes
      });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Failed to submit avatar', '', 'error');
    } finally {
      dispatch({
        type: 'SUBMIT_AVATAR_END'
      });
    }
  };
  const bioCharsLeft = 280 - bioInput.length;
  return <Modal maxWidth={600}>
      <ModalHeader>
        <div>
          <h2 className="text-lg font-semibold">Verification Session</h2>
          <p className="mt-0.5 text-xs font-normal text-textSecondary">
            Walk through the steps with the admin on the live call.
          </p>
        </div>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-5">
          {/* Step 1: Social proof. Code is never shown client-side; admin reads
              it on the call to bind the action to call presence. */}
          <section>
            <h3 className="text-sm font-semibold text-textPrimary">
              1. Social proof
            </h3>
            {session.socialProofConfirmed ? <div className="mt-2 flex items-center gap-2 rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-500">
                <Check size={16} />
                <span>Admin confirmed social proof</span>
              </div> : <p className="mt-2 text-sm text-textSecondary">
                The admin will read you a code on the call. Post it from a
                publicly-verified social account (or edit your bio / DM it). The
                admin will confirm here once they see it.
              </p>}
          </section>

          {/* Counter */}
          <section className="flex items-center justify-between gap-3 rounded-md border border-ds-border-standard px-3 py-2.5">
            <div className="text-sm">
              <span className="text-textSecondary">Approved </span>
              <span className="font-semibold text-textPrimary">
                {session.approvedCount}
              </span>
              <span className="text-textSecondary">
                {' '}
                / {freeCandidates} free + {session.paidExtras} paid
              </span>
            </div>
            {isFull && <Button text={paying ? 'Paying…' : 'Pay for another'} variant="SECONDARY" disabled={paying} loading={paying} onClick={payForExtra} />}
          </section>

          {/* Step 2: Bios */}
          <section>
            <h3 className="text-sm font-semibold text-textPrimary">
              2. Candidate bios
            </h3>
            <div className="mt-2">
              <textarea maxLength={280} value={bioInput} onChange={e_0 => dispatch({
              type: 'SET_BIO',
              payload: e_0.target.value
            })} rows={3} placeholder="Type a bio (max 280 chars)" className="w-full resize-none rounded-md bg-transparent px-3 py-2 text-sm text-textPrimary ring-1 ring-ds-border-standard transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-xs text-textSecondary">
                  {bioCharsLeft} characters left
                </span>
                <Button text={submittingBio ? 'Submitting…' : 'Submit bio'} variant="PRIMARY" disabled={submittingBio || !bioInput.trim()} loading={submittingBio} onClick={submitBio} />
              </div>
            </div>
            <CandidateList items={session.candidateBios} render={c => <span>{c.text}</span>} empty="No bios submitted yet." />
          </section>

          {/* Step 3: Avatars */}
          <section>
            <h3 className="text-sm font-semibold text-textPrimary">
              3. Candidate avatars
            </h3>
            <div className="mt-2">
              <Button text={submittingAvatar ? 'Uploading…' : 'Upload avatar'} variant="PRIMARY" icon={<ImagePlus size={16} />} disabled={submittingAvatar} loading={submittingAvatar} onClick={() => dispatch({
              type: 'OPEN_UPLOAD'
            })} />
            </div>
            <CandidateList items={session.candidateAvatars} render={c_0 => <span className="text-textSecondary">
                  Avatar · {c_0.id.slice(0, 8)}
                </span>} empty="No avatars uploaded yet." />
          </section>
        </div>
      </ModalContent>
      {uploadOpen && <Upload crop={{
      enabled: true,
      aspect: 1,
      circularCrop: true
    }} title="Crop avatar candidate" closeHandler={() => dispatch({
      type: 'CLOSE_UPLOAD'
    })} callbackFn={submitCroppedAvatar} enableCamera />}
    </Modal>;
};